import { useEffect, useMemo, useState } from 'react';
import type { Checklist } from '@/services/checklists';
import { subscribeToTripChecklist } from '@/services/checklists';
import type { ItineraryItem } from '@/services/itinerary';
import { subscribeToTripItineraryItems } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import type { TripMemoryPreview } from '@/services/tripMemories';
import { buildTripSummary, type TripSummary } from '@/services/tripSummary';
import type { TripBudgetSnapshot } from '@/types/budget';

type UseTripSummaryOptions = {
  budgetSnapshot?: TripBudgetSnapshot;
  memoryPreview?: TripMemoryPreview | null;
  coverImage?: string | null;
  enabled?: boolean;
};

export function useTripSummary(trip: Trip, options: UseTripSummaryOptions = {}): TripSummary {
  const [itineraryItems, setItineraryItems] = useState<ItineraryItem[]>([]);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled || !trip.id) {
      setItineraryItems([]);
      setChecklist(null);
      return;
    }
    const unsubscribeItinerary = subscribeToTripItineraryItems(trip.id, setItineraryItems);
    const unsubscribeChecklist = subscribeToTripChecklist(trip.id, setChecklist);
    return () => {
      unsubscribeItinerary();
      unsubscribeChecklist();
    };
  }, [enabled, trip.id]);

  return useMemo(
    () =>
      buildTripSummary(trip, {
        itineraryItems,
        checklist,
        budgetSnapshot: options.budgetSnapshot,
        memoryPreview: options.memoryPreview,
        coverImage: options.coverImage,
      }),
    [checklist, itineraryItems, options.budgetSnapshot, options.coverImage, options.memoryPreview, trip],
  );
}
